import { useCallback, useMemo } from "react";
import { useAreaMapsSearch } from "common/apis/mysociability-search";
import { resolveSuggestionArea } from "common/apis/search";
import type { SelectedLocation } from "common/hooks";
import { defaultRegion } from "common/config";

import type { OnboardingCity } from "../types";

/** Cities per country in the browse grid. */
const MAX_CITIES_PER_COUNTRY = 10;

export type OnboardingCityEntry = OnboardingCity & {
  /** Venues we hold accessibility information for. */
  count: number;
};

/** Stable identity for "which city is currently resolving". */
export const cityKey = (city: { name: string; country: string }) =>
  `${city.country}|${city.name}`;

export type OnboardingCityGroup = {
  country: string;
  cities: OnboardingCityEntry[];
};

/** Facet values arrive as "City, Country" — split on the *last* comma. */
const parseFacetValue = (value: string): OnboardingCity | null => {
  const lastComma = value.lastIndexOf(", ");
  if (lastComma === -1) return null;

  return {
    name: value.substring(0, lastComma).trim(),
    country: value.substring(lastComma + 2).trim(),
  };
};

/** Groups by country, biggest country first, cities by venue count within it. */
const groupByCountry = (
  cities: OnboardingCityEntry[],
): OnboardingCityGroup[] => {
  const byCountry = new Map<string, OnboardingCityEntry[]>();

  cities.forEach((city) => {
    const existing = byCountry.get(city.country);
    if (existing) {
      existing.push(city);
    } else {
      byCountry.set(city.country, [city]);
    }
  });

  return Array.from(byCountry.entries())
    .map(([country, group]) => ({
      country,
      cities: [...group]
        .sort((a, b) => b.count - a.count)
        .slice(0, MAX_CITIES_PER_COUNTRY),
    }))
    .sort((a, b) => b.cities.length - a.cities.length);
};

/**
 * Backs the *browse* half of the city step — the grid shown while the search
 * box is empty. Searching is served by `useCitySuggest`, which needs at least
 * one character, so the full covered-city list still comes from the locations
 * facets the home page already reads.
 *
 * `facets.locations` is the "City, Country" → venue-count index. The backend
 * counts with `mapping_status=mapped`, so those numbers mean venues *with*
 * accessibility information — which is exactly what the card subtitle claims.
 *
 * **Anchored on the hardcoded London default, not the user's position.** The
 * whole point of this step is that we have not asked where they are yet, and
 * the browse list is a global "here is everywhere we cover" grid rather than a
 * nearby-first one — the coordinates only break ties. Native passes
 * `useManagedLocation()`'s value here because by then it has one.
 */
export function useOnboardingCities() {
  const { data, isLoading, error } = useAreaMapsSearch(
    defaultRegion.latitude,
    defaultRegion.longitude,
  );

  const browseGroups = useMemo(() => {
    const cities = (data?.facets?.locations ?? [])
      .map((facet): OnboardingCityEntry | null => {
        const parsed = parseFacetValue(facet.value);
        return parsed ? { ...parsed, count: facet.count } : null;
      })
      .filter((city): city is OnboardingCityEntry => city !== null);

    return groupByCountry(cities);
  }, [data?.facets?.locations]);

  const totalCitiesWithData = useMemo(
    () => browseGroups.reduce((total, group) => total + group.cities.length, 0),
    [browseGroups],
  );

  /**
   * Coordinates for a browse card. These cities are ours, not Google's, so
   * there is no place id to resolve — it geocodes the "City, Country" string.
   * The comma matters: "Manchester United Kingdom" geocodes to the football
   * club.
   */
  const resolveCity = useCallback(
    async (city: OnboardingCity): Promise<SelectedLocation | null> => {
      const query = `${city.name}, ${city.country}`;
      const area = await resolveSuggestionArea({ query });
      if (!area) return null;

      return {
        text: area.address || query,
        simpleText: area.name || city.name,
        latitude: area.lat,
        longitude: area.lng,
        placeId: area.place_id,
      };
    },
    [],
  );

  return {
    browseGroups,
    totalCitiesWithData,
    isLoading,
    error,
    resolveCity,
  };
}
